import React from 'react';
import { Link } from 'react-router-dom';

const Research = () => {
    return (
        <div style={styles.container}>
            <header style={styles.header}>
                <div style={styles.badge}>ACADEMIC EXCELLENCE & SCHOLARSHIP</div>
                <h1 style={styles.title}>Research, Publications & Conferences</h1>
                <p style={styles.subtitle}>
                    Advancing Pedagogical Psychology, Literature Studies & Multimodal AI Language Acquisition
                </p>
            </header>

            <div style={styles.content}>
                
                {/* Metrics Highlight */}
                <div style={styles.metricsGrid}>
                    <div style={styles.metricCard}>
                        <div style={styles.metricNum}>28</div>
                        <div style={styles.metricLabel}>ISBN Research Articles</div>
                    </div>
                    <div style={styles.metricCard}>
                        <div style={styles.metricNum}>16</div>
                        <div style={styles.metricLabel}>ISSN Journal Papers</div>
                    </div>
                    <div style={styles.metricCard}>
                        <div style={styles.metricNum}>3</div>
                        <div style={styles.metricLabel}>Academic Books Edited</div>
                    </div>
                    <div style={styles.metricCard}>
                        <div style={styles.metricNum}>4x</div>
                        <div style={styles.metricLabel}>Assist World Records</div>
                    </div>
                </div>

                {/* Section 1: Research Focus Areas */}
                <section style={styles.section}>
                    <h2 style={styles.heading}>🔬 Core Research Domains</h2>
                    <p style={styles.text}>
                        Under the research supervision of <strong>Dr. S. Sabitha Shunmuga Priya</strong> (Assistant Professor in English, KGISL Institute of Technology), Arivu Nest R&D leads rigorous research across four foundational thrust areas:
                    </p>

                    <div style={styles.domainsGrid}>
                        <div style={styles.domainCard}>
                            <h3 style={styles.domainTitle}>1. Psychology of Language Acquisition</h3>
                            <p style={styles.domainText}>
                                Investigating cognitive barriers, speech hesitation, and affective filters in ESL (English as a Second Language) learners to construct high-retention teaching methodologies.
                            </p>
                        </div>
                        <div style={styles.domainCard}>
                            <h3 style={styles.domainTitle}>2. British & American Literature</h3>
                            <p style={styles.domainText}>
                                Historical literary analysis spanning Enlightenment revolutionary philosophy (Thomas Paine, republicanism, deism) to modern post-colonial and diasporic literature.
                            </p>
                        </div>
                        <div style={styles.domainCard}>
                            <h3 style={styles.domainTitle}>3. AI & Multimodal Linguistic Modeling</h3>
                            <p style={styles.domainText}>
                                Engineering real-time generative AI pipelines (Google Gemini) to deliver instantaneous pronunciation cadence analysis, semantic grading, and adaptive exercises.
                            </p>
                        </div>
                        <div style={styles.domainCard}>
                            <h3 style={styles.domainTitle}>4. Professional & Workplace Communication</h3>
                            <p style={styles.domainText}>
                                Developing practical frameworks for technical graduates to master corporate discourse, interview fluency, technical documentation, and cross-cultural presentation.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Section 2: Global Collaborations */}
                <section style={styles.section}>
                    <h2 style={styles.heading}>🌏 Global Collaborations & Conferences</h2>
                    <div style={styles.infoBox}>
                        <p style={styles.text}>
                            <strong>International Scholarly Partnerships:</strong> Arivu Nest R&D has actively collaborated on joint publications and peer reviews with esteemed international academicians and researchers across <strong>Singapore</strong> and <strong>Indonesia</strong>.
                        </p>
                        <p style={styles.text}>
                            <strong>Conferences & Symposia:</strong> Successfully organized <strong>2 International Conferences</strong> bringing together educators, linguists, and researchers from across premier Indian universities and global academic institutions.
                        </p>
                    </div>
                </section>

                {/* Section 3: Faculty Development & Workshops */}
                <section style={styles.section}>
                    <h2 style={styles.heading}>🎓 Faculty Development Programs (FDPs)</h2>
                    <p style={styles.text}>
                        We believe that elevating language education begins with empowering faculty. Arivu Nest R&D has conducted:
                    </p>
                    <ul style={styles.list}>
                        <li><strong>National-Level Faculty Development Programs (FDPs):</strong> Training professors and lecturers on modern teaching methodologies, digital humanities tools, and research paper writing.</li>
                        <li><strong>Student Capacity Building Workshops:</strong> Conducting intensive workshops on professional communication, soft skills, and public speaking across collegiate networks.</li>
                        <li><strong>World Record Program Organization:</strong> Serving 4 times on the executive organizing committee for the <em>Assist World Record Program</em>.</li>
                    </ul>
                </section>

                {/* Research Contact CTA */}
                <div style={styles.ctaBox}>
                    <h3 style={{ margin: '0 0 10px', fontSize: '20px', color: '#0f172a' }}>Collaborate on Academic Research</h3>
                    <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px' }}>
                        Interested in co-authoring papers, publishing ISBN-indexed conference proceedings, or hosting a Faculty Development Program at your institution?
                    </p>
                    <a href="https://mail.google.com/mail/?view=cm&fs=1&to=dr.sabitha@arivunest.com" target="_blank" rel="noopener noreferrer" style={styles.primaryBtn}>
                        Email Dr. Sabitha for Collaboration &rarr;
                    </a>
                </div>

            </div>
        </div>
    );
};

const styles = {
    container: { maxWidth: '1000px', margin: '0 auto', padding: 'clamp(40px, 6vw, 80px) 20px', fontFamily: "'Inter', sans-serif" },
    header: { textAlign: 'center', marginBottom: '50px' },
    badge: { display: 'inline-block', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '5px 14px', borderRadius: '999px', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '14px' },
    title: { fontSize: 'clamp(26px, 5vw, 38px)', color: '#0f172a', fontWeight: '800', margin: '0 0 10px 0', letterSpacing: '-0.5px' },
    subtitle: { fontSize: '15px', color: '#059669', fontWeight: '600', maxWidth: '750px', margin: '0 auto' },
    content: { maxWidth: '850px', margin: '0 auto' },
    metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '45px' },
    metricCard: { background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' },
    metricNum: { fontSize: '28px', fontWeight: '800', color: '#059669', marginBottom: '4px' },
    metricLabel: { fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' },
    section: { marginBottom: '45px' },
    heading: { fontSize: '22px', color: '#0f172a', fontWeight: '700', marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px' },
    text: { lineHeight: '1.75', color: '#334155', fontSize: '15px', marginBottom: '16px' },
    domainsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '18px', marginTop: '16px' },
    domainCard: { background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' },
    domainTitle: { margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700', color: '#0f172a' },
    domainText: { margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.6' },
    infoBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px' },
    list: { paddingLeft: '20px', lineHeight: '1.8', color: '#334155', fontSize: '14.5px' },
    ctaBox: { background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', border: '1.5px solid #a7f3d0', borderRadius: '14px', padding: '32px', textAlign: 'center', marginTop: '40px' },
    primaryBtn: { display: 'inline-block', background: '#059669', color: '#ffffff', textDecoration: 'none', padding: '12px 26px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)' }
};

export default Research;
